import React, { useState, useRef, useEffect } from "react";
import Draggable from "react-draggable";
import {
    Send, Mic, Paperclip, Smile, Clock, X,
    Maximize2, Minimize2, GripHorizontal, ImageIcon, FileText,
    Camera, MapPin, Contact, Sticker, Plus, Trash2
} from "lucide-react";
import EmojiPicker from 'emoji-picker-react';
import { ref as dbRef, get, push, set, remove as fbRemove, onValue } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../../../integrations/firebase';
import { useChat } from '../../hooks/useChat';

/**
 * ChatInput Component — Telegram Web Style
 * Supports: Docked (default) and Floating modes
 */

// Sticker packs (built-in emoji stickers for quick send)
const STICKER_PACKS = [
    { id: 'emotions', name: '😊 Emotions', stickers: ['😀', '😂', '🥹', '😍', '🥰', '😎', '🤩', '😭', '😤', '🤯', '🥳', '😱', '🤗', '😈', '💀', '🫠', '😴', '🤮', '🫡', '🤡'] },
    { id: 'gestures', name: '👋 Gestures', stickers: ['👋', '👍', '👎', '👏', '🙌', '🤝', '✌️', '🤞', '🫰', '👌', '🤙', '💪', '🙏', '☝️', '👆', '✋', '🫶', '❤️‍🔥', '💯', '🔥'] },
    { id: 'animals', name: '🐱 Animals', stickers: ['🐱', '🐶', '🦊', '🐻', '🐼', '🐨', '🦁', '🐸', '🐙', '🦋', '🐝', '🐳', '🦄', '🐥', '🦀', '🐿️', '🦩', '🐞', '🦎', '🐾'] },
    { id: 'food', name: '🍕 Food', stickers: ['🍕', '🍔', '🌮', '🍜', '🍣', '🍩', '🍰', '☕', '🧋', '🍷', '🍻', '🧁', '🍪', '🍿', '🥐', '🌶️', '🍉', '🍓', '🥑', '🧀'] },
    { id: 'objects', name: '⚡ Objects', stickers: ['⚡', '🎯', '💎', '🏆', '🎸', '🎮', '🚀', '🌈', '⭐', '❤️', '💜', '💙', '💚', '💛', '🧡', '🤍', '🖤', '💝', '🎁', '🎉'] },
];

const ChatInput = ({
    inputText,
    handleTyping,
    sendMessage,

    isRecording,
    onStartRecording,
    onStopRecording,
    fileInputRef,
    handleFileUpload,
    onPasteImage,
    setShowScheduleModal,
    replyTo,
    setReplyTo,
    activeChatName
}) => {
    const { currentUser } = useChat();
    const userId = currentUser?.uid || currentUser?.id;
    const [isFloating, setIsFloating] = useState(false);
    const [bounds, setBounds] = useState({ left: 0, top: 0, right: 0, bottom: 0 });
    const [showAttachMenu, setShowAttachMenu] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [pickerTab, setPickerTab] = useState('emoji'); // 'emoji' | 'stickers'
    const [activeStickerPack, setActiveStickerPack] = useState(0);
    const [customStickers, setCustomStickers] = useState([]);
    const [uploadingSticker, setUploadingSticker] = useState(false);
    const stickerUploadRef = useRef(null);
    const draggingRef = useRef(null);
    const inputRef = useRef(null);
    const nodeRef = useRef(null);

    // Load custom stickers from Firebase
    useEffect(() => {
        if (!userId) return;
        const stickersRef = dbRef(db, `users/${userId}/stickers`);
        const unsub = onValue(stickersRef, (snap) => {
            if (snap.exists()) {
                const data = snap.val();
                const arr = Object.entries(data).map(([key, val]) => ({
                    id: key,
                    url: typeof val === 'string' ? val : val.url,
                    name: typeof val === 'object' ? val.name : 'Sticker'
                }));
                setCustomStickers(arr);
            } else {
                setCustomStickers([]);
            }
        });
        return () => unsub();
    }, [userId]);

    // Upload custom sticker PNG
    const handleStickerUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !userId) return;
        if (!file.type.startsWith('image/')) return;
        if (file.size > 2 * 1024 * 1024) {
            // Max 2MB
            return;
        }
        setUploadingSticker(true);
        try {
            const fileRef = storageRef(storage, `stickers/${userId}/${Date.now()}_${file.name}`);
            await uploadBytes(fileRef, file);
            const url = await getDownloadURL(fileRef);
            // Save to user's sticker collection
            const stickerRef = push(dbRef(db, `users/${userId}/stickers`));
            await set(stickerRef, { url, name: file.name, createdAt: Date.now() });
        } catch (err) {
            console.error('Sticker upload failed:', err);
        } finally {
            setUploadingSticker(false);
            if (stickerUploadRef.current) stickerUploadRef.current.value = '';
        }
    };

    // Delete custom sticker
    const handleDeleteSticker = async (stickerId) => {
        if (!userId) return;
        try {
            await fbRemove(dbRef(db, `users/${userId}/stickers/${stickerId}`));
        } catch (err) {
            console.error('Sticker delete failed:', err);
        }
    };

    // Send custom sticker as image message
    const sendCustomSticker = (stickerUrl) => {
        sendMessage('sticker', stickerUrl, 'sticker', null);
        setShowEmojiPicker(false);
    };

    const handleSendClick = () => {
        sendMessage();
        setTimeout(() => {
            inputRef.current?.focus();
        }, 100);
    };

    const onEmojiClick = (emojiObject) => {
        const emoji = emojiObject.emoji;
        const newText = inputText + emoji;
        handleTyping({ target: { value: newText, style: {} } });
    };

    const onStickerClick = (sticker) => {
        // Send sticker as a message directly
        const newText = sticker;
        handleTyping({ target: { value: newText, style: {} } });
        setTimeout(() => sendMessage(), 50);
        setShowEmojiPicker(false);
    };

    // Detect mobile to force docked mode
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

    useEffect(() => {
        if (isMobile) {
            setIsFloating(false);
        }
    }, [isMobile]);

    const toggleMode = () => {
        setIsFloating(!isFloating);
    };

    // Click Outside for Emoji Picker & Attach Menu
    const emojiPickerRef = useRef(null);
    const attachMenuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
                setShowEmojiPicker(false);
            }
            if (attachMenuRef.current && !attachMenuRef.current.contains(event.target)) {
                setShowAttachMenu(false);
            }
        };

        if (showEmojiPicker || showAttachMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showEmojiPicker, showAttachMenu]);

    // Stickers Panel
    const renderStickersPanel = () => (
        <div className="w-[340px] bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden">
            {/* Sticker Pack Tabs */}
            <div className="flex items-center gap-1 p-2 border-b border-slate-100 overflow-x-auto custom-scrollbar">
                {STICKER_PACKS.map((pack, i) => (
                    <button
                        key={pack.id}
                        onClick={() => setActiveStickerPack(i)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                            activeStickerPack === i
                                ? 'bg-indigo-100 text-indigo-700'
                                : 'text-slate-500 hover:bg-slate-50'
                        }`}
                    >
                        {pack.name}
                    </button>
                ))}
            </div>
            {/* Sticker Grid */}
            <div className="p-3 grid grid-cols-5 gap-1 max-h-[280px] overflow-y-auto custom-scrollbar">
                {STICKER_PACKS[activeStickerPack].stickers.map((sticker, i) => (
                    <button
                        key={i}
                        onClick={() => onStickerClick(sticker)}
                        className="w-14 h-14 flex items-center justify-center text-3xl rounded-xl hover:bg-indigo-50 hover:scale-110 transition-all duration-150 active:scale-95"
                    >
                        {sticker}
                    </button>
                ))}
            </div>
            {/* Custom Stickers Section */}
            <div className="border-t border-slate-100 p-3">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-slate-500">My Stickers</span>
                    <button
                        onClick={() => stickerUploadRef.current?.click()}
                        className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
                    >
                        <Plus size={12} /> Upload PNG
                    </button>
                    <input
                        ref={stickerUploadRef}
                        type="file"
                        accept="image/png,image/gif,image/webp"
                        className="hidden"
                        onChange={handleStickerUpload}
                    />
                </div>
                {uploadingSticker && (
                    <div className="flex items-center gap-2 text-xs text-indigo-500 mb-2">
                        <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                        Uploading...
                    </div>
                )}
                {customStickers.length > 0 ? (
                    <div className="grid grid-cols-4 gap-1.5 max-h-[120px] overflow-y-auto custom-scrollbar">
                        {customStickers.map((sticker) => (
                            <div key={sticker.id} className="relative group">
                                <button
                                    onClick={() => sendCustomSticker(sticker.url)}
                                    className="w-full aspect-square rounded-xl overflow-hidden bg-slate-50 hover:bg-indigo-50 hover:scale-105 transition-all duration-150 border border-slate-100"
                                >
                                    <img src={sticker.url} alt="" className="w-full h-full object-contain p-1" />
                                </button>
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleDeleteSticker(sticker.id); }}
                                    className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                    <X size={8} strokeWidth={3} />
                                </button>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-[10px] text-slate-400 text-center py-4">Upload PNG images to create custom stickers</p>
                )}
            </div>
        </div>
    );

    const content = (
        <div className={`
            flex flex-col
            ${isFloating ? "h-full" : ""}
        `}>
            {/* DRAG HANDLE (Only in floating) */}
            {isFloating && (
                <div className="w-full h-4 bg-slate-50 rounded-t-xl cursor-move flex items-center justify-center border-b border-slate-100 group">
                    <div className="w-12 h-1 bg-slate-300 rounded-full group-hover:bg-indigo-400 transition-colors" />
                </div>
            )}

            {/* REPLY PREVIEW — Telegram style */}
            {replyTo && (
                <div className="flex items-center gap-3 mx-3 mt-2 p-2.5 bg-white rounded-xl border-l-[3px] border-indigo-500 shadow-sm animate-in slide-in-from-bottom-2 fade-in">
                    <div className="flex-1 min-w-0">
                        <span className="font-semibold text-indigo-600 text-xs block">
                            Replying to {replyTo.senderId === "me" ? "Yourself" : activeChatName}
                        </span>
                        <p className="text-xs text-slate-500 truncate mt-0.5">{replyTo.type === 'text' ? replyTo.text : `[${replyTo.type}]`}</p>
                    </div>
                    <button onClick={() => setReplyTo(null)} className="p-1.5 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition-colors">
                        <X size={14} />
                    </button>
                </div>
            )}

            {/* MAIN INPUT BAR — Enterprise Hub Floating Composer */}
            {/* mx-6 on a 360px docked window leaves the input a stub; the dock already
                provides the frame, so compact mode hugs the edges. */}
            <div className={`glass-morphism border border-slate-200/30 shadow-lg flex items-end gap-2 group focus-within:shadow-xl transition-all duration-300 ${isFloating
                ? "flex-1 rounded-2xl p-2 mx-3 mb-3"
                : "rounded-3xl p-3 mx-6 mb-4"}`}>

                {/* ADD BUTTON */}
                <button
                    onClick={() => fileInputRef.current?.click()}
                    className="p-2.5 text-slate-400 hover:bg-slate-100 rounded-full transition-all"
                >
                    <span className="material-symbols-outlined">add_circle</span>
                </button>

                {/* EMOJI/STICKER BUTTON */}
                <div className="relative mb-1">
                    <button
                        onClick={() => {
                            setShowEmojiPicker(!showEmojiPicker);
                            setShowAttachMenu(false);
                        }}
                        className={`p-2 rounded-full transition-all duration-200 ${
                            showEmojiPicker
                                ? 'bg-indigo-100 text-indigo-600 rotate-12'
                                : 'text-slate-400 hover:text-indigo-500 hover:bg-slate-100'
                        }`}
                    >
                        <Smile size={22} />
                    </button>

                    {/* EMOJI/STICKER PICKER — Floating Above */}
                    {showEmojiPicker && (
                        <div ref={emojiPickerRef} className="absolute bottom-14 left-0 z-50 animate-in fade-in slide-in-from-bottom-3 duration-200">
                            {/* Tabs */}
                            <div className="bg-white rounded-t-2xl border border-b-0 border-slate-200 shadow-lg flex">
                                <button
                                    onClick={() => setPickerTab('emoji')}
                                    className={`flex-1 py-2.5 text-xs font-semibold transition-colors rounded-tl-2xl ${
                                        pickerTab === 'emoji'
                                            ? 'bg-indigo-50 text-indigo-600 border-b-2 border-indigo-600'
                                            : 'text-slate-400 hover:text-slate-600'
                                    }`}
                                >
                                    😊 Emojis
                                </button>
                                <button
                                    onClick={() => setPickerTab('stickers')}
                                    className={`flex-1 py-2.5 text-xs font-semibold transition-colors rounded-tr-2xl ${
                                        pickerTab === 'stickers'
                                            ? 'bg-indigo-50 text-indigo-600 border-b-2 border-indigo-600'
                                            : 'text-slate-400 hover:text-slate-600'
                                    }`}
                                >
                                    🎭 Stickers
                                </button>
                            </div>
                            {/* Content */}
                            {pickerTab === 'emoji' ? (
                                <div className="shadow-2xl rounded-b-2xl overflow-hidden">
                                    <EmojiPicker
                                        onEmojiClick={onEmojiClick}
                                        autoFocusSearch={false}
                                        theme="light"
                                        searchDisabled={false}
                                        skinTonesDisabled
                                        width={340}
                                        height={360}
                                        previewConfig={{ showPreview: false }}
                                    />
                                </div>
                            ) : (
                                renderStickersPanel()
                            )}
                        </div>
                    )}
                </div>

                {/* TEXT INPUT — Mockup rounded field */}
                <div className="flex-1 overflow-hidden">
                    <textarea
                        ref={inputRef}
                        autoFocus={true}
                        value={inputText}
                        onChange={(e) => {
                            handleTyping(e);
                            e.target.style.height = 'inherit';
                            e.target.style.height = `${Math.min(e.target.scrollHeight, 128)}px`;
                        }}
                        onPaste={(e) => {
                            const items = e.clipboardData?.items;
                            if (!items) return;
                            for (const item of items) {
                                if (item.type.startsWith('image/')) {
                                    e.preventDefault();
                                    const file = item.getAsFile();
                                    if (file && onPasteImage) {
                                        onPasteImage(file);
                                    }
                                    return;
                                }
                            }
                        }}
                        placeholder={`Message ${activeChatName || ''}...`}
                        className="w-full bg-transparent border-none focus:ring-0 focus:outline-none max-h-32 min-h-[24px] px-2 py-2 text-sm resize-none placeholder:text-slate-400 leading-relaxed custom-scrollbar"
                        rows={1}
                        style={{ overflow: 'hidden' }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                sendMessage();
                                setTimeout(() => inputRef.current?.focus(), 10);
                            }
                        }}
                    />
                </div>

                {/* RIGHT ACTIONS */}
                <div className="flex items-center gap-0.5 mb-0.5">
                    {/* ATTACH BUTTON */}
                    <div className="relative" ref={attachMenuRef}>
                        <button
                            onClick={() => {
                                setShowAttachMenu(!showAttachMenu);
                                setShowEmojiPicker(false);
                            }}
                            className={`p-2 rounded-full transition-all duration-200 ${
                                showAttachMenu
                                    ? 'bg-indigo-100 text-indigo-600 rotate-45'
                                    : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
                            }`}
                        >
                            <Paperclip size={21} />
                        </button>

                        {/* ATTACH MENU — Telegram Style Popup */}
                        {showAttachMenu && (
                            <div className="absolute bottom-14 right-0 bg-white rounded-2xl shadow-2xl border border-slate-100 p-2 animate-in slide-in-from-bottom-3 fade-in z-50 min-w-[200px]">
                                <button
                                    onClick={() => { fileInputRef?.current?.click(); setShowAttachMenu(false); }}
                                    className="flex items-center gap-3 p-2.5 hover:bg-slate-50 rounded-xl text-sm text-slate-700 font-medium w-full transition-colors"
                                >
                                    <div className="p-2 bg-blue-500 text-white rounded-xl"><ImageIcon size={16} /></div>
                                    Photo or Video
                                </button>
                                <button
                                    onClick={() => { fileInputRef?.current?.click(); setShowAttachMenu(false); }}
                                    className="flex items-center gap-3 p-2.5 hover:bg-slate-50 rounded-xl text-sm text-slate-700 font-medium w-full transition-colors"
                                >
                                    <div className="p-2 bg-violet-500 text-white rounded-xl"><FileText size={16} /></div>
                                    Document
                                </button>
                                <button
                                    onClick={() => setShowScheduleModal(true)}
                                    className="flex items-center gap-3 p-2.5 hover:bg-slate-50 rounded-xl text-sm text-slate-700 font-medium w-full transition-colors"
                                >
                                    <div className="p-2 bg-amber-500 text-white rounded-xl"><Clock size={16} /></div>
                                    Schedule Message
                                </button>
                            </div>
                        )}
                    </div>

                    {/* SEND / MIC BUTTON — Enterprise Hub style */}
                    {inputText.trim() || isRecording ? (
                        <button
                            onClick={handleSendClick}
                            className="w-10 h-10 primary-gradient text-white rounded-full flex items-center justify-center hover:shadow-lg transition-all scale-100 hover:scale-105 active:scale-95 shrink-0"
                        >
                            <span className="material-symbols-outlined text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>send</span>
                        </button>
                    ) : (
                        <button
                            onMouseDown={onStartRecording}
                            onMouseUp={onStopRecording}
                            onTouchStart={onStartRecording}
                            onTouchEnd={onStopRecording}
                            className={`p-2.5 rounded-full transition-all duration-200 w-11 h-11 flex items-center justify-center shrink-0 ${
                                isRecording
                                    ? 'bg-red-500 text-white animate-pulse shadow-lg shadow-red-500/30'
                                    : 'text-slate-400 hover:text-indigo-600 hover:bg-indigo-50'
                            }`}
                            title="Hold to Record"
                        >
                            <Mic size={21} />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );

    if (isFloating && !isMobile) {
        return (
            <Draggable bounds="parent" handle=".cursor-move" nodeRef={nodeRef}>
                <div
                    ref={nodeRef}
                    className="absolute bottom-20 right-10 z-50 bg-white/95 backdrop-blur-xl border border-slate-200/50 rounded-2xl shadow-2xl w-[90%] max-w-[600px] min-w-[320px] resize overflow-hidden"
                >
                    {content}
                </div>
            </Draggable>
        );
    }

    // Docked Mode — Glass morphism bottom bar matching mockup
    return (
        <div className="w-full z-30 shrink-0 pb-safe">
            {content}
        </div>
    );
};

export default ChatInput;
